import { z } from "zod";

/**
 * Commonwealth Rent Assistance (CRA) — estimator + attach-checker.
 *
 * CRA is a Commonwealth supplement administered by Services Australia. It is NOT
 * a standalone payment: it attaches to a qualifying primary payment (a social
 * security pension/allowance, or **Family Tax Benefit Part A** — the
 * under-recognised route for working renting families). It is paid at
 * **75c for every $1 of rent above a minimum threshold, up to a per-situation
 * maximum** (DSS Social Security Guide 5.1.7.10).
 *
 * Two surfaces live here:
 *  - `rentAssistanceEstimate` — the 75c taper from family situation + rent.
 *  - `attachCheck` — a qualitative triage: is CRA *likely able* to attach to the
 *    user's situation, and the reminder that Services Australia only assesses it
 *    once current rent has been reported.
 *
 * VERSIONING — DATE-KEYED, not FY-keyed. CRA rates index biannually on
 * **20 March and 20 September** (CPI). A new rate file (`rates-YYYY-MM-DD.ts`) is
 * authored per indexation date and never mutated after shipping; historical
 * estimates resolve against the rates in force on the assessed date.
 *
 * HARD RULES (regulatory guardrails, encoded — see panel + validation §3/§4):
 *  - Information, not advice. No "claim $X", no "you're owed", no product handoff.
 *  - CRA is a government supplement: **$0 affiliate, no monetisation surface** —
 *    nothing here routes a personalised result to a financial product (the AFSL
 *    kill-line). There is no `handoff` field by construction.
 *  - The attach-checker stays **strictly qualitative** ("you *may* be able to get
 *    CRA — here is the official step"), never a quantified or implied entitlement.
 *  - The under-claim/leakage population is UNVERIFIED: we never assert "many are
 *    missing it" as a number. The framing is "easy to check you're not".
 */

/**
 * Family-situation key for the CRA rate matrix. Each row carries its own minimum
 * rent threshold + maximum CRA. Singles who share accommodation get a lower max
 * ("single sharer"). With-children rates run via FTB Part A.
 */
export const CRA_SITUATIONS = [
  "single_no_children",
  "single_sharer_no_children",
  "couple_no_children",
  "single_with_children",
  "couple_with_children",
] as const;
export type CraSituation = (typeof CRA_SITUATIONS)[number];

/**
 * One row of the CRA rate matrix: the fortnightly minimum-rent threshold below
 * which $0 is payable, and the per-fortnight maximum CRA for the situation.
 * Both in dollars, per fortnight (CRA's native cadence).
 */
export interface CraRateRow {
  /** Fortnightly rent at/below which no CRA is payable. */
  minRentThreshold: number;
  /** Maximum CRA payable per fortnight for this situation. */
  maxRate: number;
}

/** Provenance + audit metadata for a date-keyed CRA rate period. */
export interface CraRateSetMeta {
  /** Inclusive ISO start date these rates take effect (an indexation date). */
  effectiveFrom: string;
  /**
   * Inclusive ISO end date, or `null` for the open-ended current period (until
   * the next indexation supersedes it).
   */
  effectiveTo: string | null;
  /** Human label, e.g. "CRA rates effective 20 March 2026". */
  label: string;
  /** Source-of-truth URLs the figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;
}

/** A complete CRA rate set for one indexation period (date-keyed). */
export interface CraRateSet extends CraRateSetMeta {
  /**
   * Cents of CRA paid per dollar of rent above the minimum threshold. The
   * long-standing CRA taper is **0.75** (75c/$1). Encoded (not hard-coded in the
   * calc) so a future change is a new rate file, not a code edit.
   */
  taperRate: number;
  /** Per-situation thresholds + maxima. */
  rows: Record<CraSituation, CraRateRow>;
}

// --- Estimator surface --------------------------------------------------------

export const RentAssistanceInputSchema = z.object({
  /** Family situation — selects the threshold + maximum row. */
  situation: z.enum(CRA_SITUATIONS),
  /** Rent paid per fortnight, in dollars. */
  fortnightlyRent: z.number().min(0),
});

export type RentAssistanceInput = z.infer<typeof RentAssistanceInputSchema>;

/** Which constraint is binding on the estimate (drives the "why" copy). */
export type CraBand = "below_threshold" | "in_taper" | "at_maximum";

export interface RentAssistanceOutput {
  /** The situation the estimate ran against. */
  situation: CraSituation;
  /** Rent per fortnight the estimate ran against. */
  fortnightlyRent: number;
  /** Minimum-rent threshold for this situation, per fortnight. */
  minRentThreshold: number;
  /** Maximum CRA for this situation, per fortnight. */
  maxRate: number;
  /** The taper rate applied (0.75 = 75c/$1). */
  taperRate: number;
  /** Estimated CRA per fortnight: min((rent − threshold) × taper, max), ≥ 0. */
  estimatePerFortnight: number;
  /** Which constraint is binding (below threshold / tapering / capped). */
  band: CraBand;
  /**
   * Fortnightly rent at which CRA would START (= the threshold). When rent is at
   * or below the threshold this is what rent must exceed to get any CRA.
   */
  rentToStart: number;
  /**
   * Fortnightly rent at which the maximum is reached (threshold + max/taper).
   * Above this, extra rent adds no CRA.
   */
  rentToReachMax: number;
}

// --- Attach-checker surface ---------------------------------------------------

/**
 * The user's primary-payment situation. CRA attaches to a qualifying social
 * security payment OR to Family Tax Benefit Part A — the latter being the route
 * working families miss. `none` = not on any qualifying payment (CRA can't
 * attach). `unsure` = the user doesn't know (treated as "may be able to").
 */
export const PRIMARY_PAYMENT_STATUS = [
  "qualifying_income_support", // JobSeeker, pension, Youth Allowance/Austudy, Parenting Payment, etc.
  "ftb_part_a", // the under-recognised gateway
  "none", // no qualifying payment
  "unsure",
] as const;
export type PrimaryPaymentStatus = (typeof PRIMARY_PAYMENT_STATUS)[number];

/**
 * Housing arrangements that change whether CRA can attach. Modelled as explicit
 * branches (panel feasibility note): public/state housing tenants do not get
 * CRA; board/lodging and sharer arrangements are eligibility forks the verdict
 * must not gloss over.
 */
export const HOUSING_ARRANGEMENT = [
  "private_rental", // standard private rent — CRA can attach
  "public_housing", // government/community housing — CRA does NOT attach
  "board_or_lodging", // board & lodging — special two-thirds rule, edge case
  "other", // boarder/relative/non-standard — flag for SA check
] as const;
export type HousingArrangement = (typeof HOUSING_ARRANGEMENT)[number];

export const AttachCheckInputSchema = z.object({
  /** What primary payment (if any) the user receives. */
  paymentStatus: z.enum(PRIMARY_PAYMENT_STATUS),
  /** The user's housing arrangement. */
  housing: z.enum(HOUSING_ARRANGEMENT).default("private_rental"),
  /**
   * Whether the user pays rent above the minimum threshold (the user's own
   * yes/no, not a dollar figure — dollars live in the estimator). When `false`
   * we note CRA may compute to $0 even where it attaches.
   */
  payingRentAboveMinimum: z.boolean().default(true),
  /**
   * Whether the user has told Services Australia their CURRENT rent. CRA is only
   * assessed once SA holds current rent details — the core "leak" mechanic.
   */
  hasReportedRent: z.boolean().default(false),
});

export type AttachCheckInput = z.infer<typeof AttachCheckInputSchema>;
/** Pre-parse shape (defaulted fields optional) — accepted at the call boundary. */
export type AttachCheckRawInput = z.input<typeof AttachCheckInputSchema>;

/**
 * Qualitative attach verdict. STRICTLY qualitative by design — never an
 * entitlement or a dollar figure (regulatory hard rule).
 *  - `may_attach` — a qualifying gateway is present and nothing blocks it.
 *  - `cannot_attach` — a hard blocker is present (no qualifying payment, or
 *    public housing).
 *  - `check_with_services_australia` — an edge case (board/lodging, "other"
 *    housing, or "unsure" payment) where only Services Australia can confirm.
 */
export type AttachVerdict =
  | "may_attach"
  | "cannot_attach"
  | "check_with_services_australia";

export interface AttachCheckOutput {
  /** The qualitative verdict (never a dollar amount or guaranteed entitlement). */
  verdict: AttachVerdict;
  /**
   * Whether the user appears to have a qualifying gateway payment (income
   * support or FTB Part A). Informational; does not by itself mean CRA is due.
   */
  hasGateway: boolean;
  /** True when the gateway is specifically FTB Part A (the missed route). */
  viaFtbPartA: boolean;
  /**
   * The key action: CRA is only assessed once current rent is reported. True
   * when the user has a gateway but has NOT told Services Australia their rent.
   */
  needsToReportRent: boolean;
  /** True when an edge-case branch (board/lodging, other, unsure) was hit. */
  isEdgeCase: boolean;
  /** Machine-readable reason codes behind the verdict (for copy + audit). */
  reasons: readonly AttachReason[];
}

/** Reason codes driving an attach verdict — surfaced as cited copy. */
export type AttachReason =
  | "no_qualifying_payment"
  | "public_housing_excluded"
  | "gateway_income_support"
  | "gateway_ftb_part_a"
  | "rent_not_reported"
  | "rent_may_be_below_threshold"
  | "edge_case_board_lodging"
  | "edge_case_other_housing"
  | "payment_status_unsure";
